<template>
    <!-- only generate 1x variants — without this, HiDPI devices request 2x images which can be very large on small viewports -->
    <NuxtImg
        v-if="!intersectionLazy"
        densities="1"
        v-bind="$attrs"
    />
    
    <div
        v-else
        ref="container"
        :class="containerClass || 'flex flex-col items-center w-full h-full'"
    >
        <NuxtImg
            v-if="loaded"
            v-show="imageLoaded"
            v-bind="imageAttrs"
            @load="imageLoaded = true"
            @error="imageLoaded = true"
        />
        <div
            v-if="!imageLoaded"
            class="flex items-center justify-center bg-transparent rounded min-h-[100px] max-w-full max-h-full w-full"
            :style="placeholderStyle"
        >
            <slot name="loading-placeholder">
                <div class="w-6 h-6 border-2 border-gray-300 border-t-gray-600 rounded-full animate-spin" />
            </slot>
        </div>
    </div>
</template>

<script setup lang="ts">
defineOptions({
    inheritAttrs: false
})

interface Props {
    intersectionLazy?: boolean
    rootMargin?: string
    threshold?: number
    aspectRatio?: string
    containerClass?: string
    preload?: boolean
}

const props = withDefaults(defineProps<Props>(), {
    intersectionLazy: false,
    rootMargin: '50px',
    threshold: 0.1,
    aspectRatio: undefined,
    preload: false
})

const container = ref<HTMLElement>()
const loaded = ref(false)
const imageLoaded = ref(false)

const attrs = useAttrs()
const img = useImage()

// Custom preload implementation — NuxtImg's built-in `preload` prop only adds
// `imagesrcset` for density descriptors ("x,") but not for width descriptors ("w"),
// so the browser ignores `imagesizes` and always fetches the largest href variant.
// We reproduce the same URL generation as NuxtImg using img.getSizes() with matching
// modifiers and densities so the preloaded URL is identical to the rendered srcset entry.
if (import.meta.server) {
    useHead(() => {
        if (!props.preload || !attrs.src) return {}

        // Start with format: undefined to override the provider's defaultModifiers.format = 'webp'.
        // NuxtImg passes format as undefined when not set, causing the spread to neutralise the
        // default and omit f:webp from the URL. We mirror that behaviour here.
        // Mirror useImageProps() from NuxtImg exactly — all keys must be own properties
        // (even when undefined) so the provider spread overrides defaultModifiers.
        // Without explicit undefined keys, defaultModifiers.fit='fill' → rt:fill
        // and defaultModifiers.format='webp' → f:webp appear in the URL.
        const modifiers: Record<string, unknown> = {
            ...((attrs.modifiers as Record<string, unknown>) || {}),
            width: attrs.width ? Number(attrs.width) : undefined,
            height: attrs.height ? Number(attrs.height) : undefined,
            format: (attrs.format as string) || undefined,
            quality: undefined,
            background: (attrs.background as string) || undefined,
            fit: (attrs.fit as string) || undefined,
        }

        if (attrs.sizes) {
            // Responsive image: generate srcset + sizes so the browser picks the right
            // variant for the actual viewport — same as the rendered <img srcset sizes>.
            // densities: '1' matches the hardcoded densities="1" on NuxtImg to avoid
            // generating 2x variants (which would load very large images on HiDPI screens).
            const sizesResult = img.getSizes(attrs.src as string, {
                sizes: attrs.sizes as string,
                densities: '1',
                modifiers,
            })
            return {
                link: [{
                    rel: 'preload',
                    as: 'image',
                    href: sizesResult.src,
                    imagesrcset: sizesResult.srcset,
                    imagesizes: sizesResult.sizes,
                }],
            }
        }

        // Non-responsive: single optimised URL
        return {
            link: [{ rel: 'preload', as: 'image', href: img(attrs.src as string, modifiers) }],
        }
    })
}

// Filter out custom props using useAttrs
const customProps = ['intersectionLazy', 'rootMargin', 'threshold', 'aspectRatio', 'containerClass', 'preload']

const imageAttrs = computed(() => {
    const filteredAttrs = {} as Record<string, unknown>
    
    for (const [key, value] of Object.entries(attrs)) {
        if (!customProps.includes(key)) {
            filteredAttrs[key] = value
        }
    }

    // When using intersectionLazy, ensure the image loads eagerly after intersection
    // to avoid double lazy loading (IntersectionObserver already delayed the render)
    if (props.intersectionLazy && !filteredAttrs.loading) {
        filteredAttrs.loading = 'eager'
    }

    if (!filteredAttrs.densities) {
        filteredAttrs.densities = '1'
    }

    return filteredAttrs
})

const placeholderStyle = computed(() => {
    const styles: Record<string, string> = {}
    
    if (attrs.width) {
        styles.width = `${attrs.width}px`
    } else {
        styles.width = '100%'
    }
    
    if (attrs.height) {
        styles.height = `${attrs.height}px`
    }
    
    // Use aspectRatio prop or calculate from width/height
    if (props.aspectRatio) {
        styles.aspectRatio = props.aspectRatio
    } else if (attrs.width && attrs.height) {
        styles.aspectRatio = `${attrs.width} / ${attrs.height}`
    }
    
    return styles
})

onMounted(() => {
    if (props.intersectionLazy && container.value) {
        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0]?.isIntersecting) {
                    loaded.value = true
                    observer.disconnect()
                }
            },
            {
                rootMargin: props.rootMargin,
                threshold: props.threshold
            }
        )
        observer.observe(container.value)
    }
})
</script>
